'use client'
import type { ReactNode } from 'react'
import { Textarea, type TextareaProps } from '@chakra-ui/react'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import {
  FormDescription,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  FormMessage
} from '../FormField'

interface GrandFormTextareaFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends Omit<TextareaProps, 'name'> {
  name: TName
  label?: ReactNode
  description?: ReactNode
  tooltip?: ReactNode
  placeholder?: string
  required?: boolean
  optional?: boolean
  disabled?: boolean
  rules?: RegisterOptions<TFieldValues, TName>
  control?: Control<TFieldValues>
}

export const GrandFormTextareaField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  description,
  tooltip,
  placeholder,
  required,
  optional,
  disabled,
  rules,
  control,
  ...textareaProps
}: GrandFormTextareaFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      optional={optional}
      required={required}
      rules={rules}
      render={({ field }) => (
        <FormFieldControl disabled={disabled}>
          {label ? <FormFieldLabel tooltip={tooltip}>{label}</FormFieldLabel> : null}

          <Textarea
            {...field}
            disabled={disabled}
            placeholder={placeholder}
            value={field.value ?? ''}
            {...textareaProps}
          />

          {description ? <FormDescription>{description}</FormDescription> : null}

          <FormMessage />
        </FormFieldControl>
      )}
    />
  )
}
